import { useEffect, useState, useCallback } from "react";
import { Search, ArrowUpDown, ChevronDown, ChevronUp, RotateCcw } from "lucide-react";
import { socket } from "../../utils/socket";
import { AdminBookingAPI } from "../../services/adminBooking.api";
import { AdminEventAPI } from "../../services/adminEvent.api";
import BookingTable from "./components/BookingTable";

/* ======================================================
   BOOKING SECTION
====================================================== */
function BookingSection({
  title,
  icon,
  status,
  color,
  search,
  eventId,
  sort,
  defaultCollapsed = false,
  refreshKey = 0,
  onAction,
}) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState(null);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const LIMIT = 10;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AdminBookingAPI.list({
        paymentStatus: status,
        search: search || undefined,
        eventId: eventId || undefined,
        sort,
        page,
        limit: LIMIT,
      });
      const payload = res.data?.data || {};
      setBookings(payload.items || []);
      setMeta(payload.meta || null);
    } catch (err) {
      console.error("Fetch bookings failed:", err);
    } finally {
      setLoading(false);
    }
  }, [status, search, eventId, sort, page, refreshKey]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [search, eventId, sort, refreshKey]);

  return (
    <div className={`booking-section booking-section--${color}`}>
      <button
        className="booking-section__header"
        onClick={() => setCollapsed((c) => !c)}
      >
        <div className="booking-section__header-left">
          <span className={`section-dot section-dot--${color}`} />
          <span className="section-title">
            <span className="section-icon">{icon}</span>
            {title}
          </span>
          <span className={`section-badge section-badge--${color}`}>
            {loading ? "…" : (meta?.total ?? 0)}
          </span>
        </div>
        <span className="section-toggle">
          {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
        </span>
      </button>

      {!collapsed && (
        <div className="booking-section__body">
          {loading ? (
            <div className="section-empty">
              <RotateCcw size={14} className="spin" />
              <span>Memuat...</span>
            </div>
          ) : bookings.length === 0 ? (
            <div className="section-empty">Tidak ada data</div>
          ) : (
            <>
              {bookings.map((b) => (
                <BookingTable
                  key={b._id}
                  booking={b}
                  onAction={onAction}
                />
              ))}

              {meta && meta.totalPages > 1 && (
                <div className="section-pagination">
                  <span className="section-pagination__info">
                    {meta.page} / {meta.totalPages}
                  </span>
                  <div className="section-pagination__btns">
                    <button
                      disabled={page === 1}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      ← Prev
                    </button>
                    <button
                      disabled={page === meta.totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ======================================================
   ADMIN BOOKINGS
====================================================== */
export default function AdminBookings() {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [eventId, setEventId] = useState("");
  const [events, setEvents] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const [stats, setStats] = useState({
    totalBookings: 0,
    paid: 0,
    pending: 0,
    expired: 0,
    failed: 0,
    revenueMonth: 0,
    revenueAllTime: 0,
  });

  /* ===== FETCH EVENTS FOR FILTER ===== */
  useEffect(() => {
    AdminEventAPI.list()
      .then((res) => {
        const data = res.data?.data;
        const list = Array.isArray(data) ? data : data?.items || [];
        setEvents(list);
      })
      .catch(() => {});
  }, []);

  /* ===== FETCH STATS ===== */
  const fetchStats = useCallback(async () => {
    try {
      const res = await AdminBookingAPI.stats();
      setStats(res.data?.data || {});
    } catch (err) {
      console.error("Fetch stats failed");
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  /* ===== SOCKET ===== */
  useEffect(() => {
    if (!socket.connected) socket.connect();

    const token = localStorage.getItem("adminToken");

    const onConnect = () => {
      if (token) socket.emit("admin:join", token);
    };

    if (socket.connected) onConnect();
    socket.on("connect", onConnect);

    const refreshList = () => setRefreshKey((k) => k + 1);

    socket.on("admin:booking-stats-updated", (data) => {
      setStats((prev) => ({ ...prev, ...data }));
    });

    // Refresh list ketika ada booking baru, upload bukti, atau status berubah
    socket.on("admin:booking-list-updated", refreshList);
    socket.on("booking:updated", refreshList);
    socket.on("booking:verified", refreshList);

    return () => {
      socket.off("connect", onConnect);
      socket.off("admin:booking-stats-updated");
      socket.off("admin:booking-list-updated", refreshList);
      socket.off("booking:updated", refreshList);
      socket.off("booking:verified", refreshList);
    };
  }, []);

  const fmtRupiah = (n) =>
    `Rp ${(n || 0).toLocaleString("id-ID")}`;

  return (
    <div className="admin-bookings">
      {/* HEADER */}
      <div className="admin-bookings__header">
        <div>
          <h1>Bookings</h1>
          <p>Monitor semua transaksi dan tiket</p>
        </div>
      </div>

      {/* STATS */}
      <div className="admin-bookings__stats">
        <StatCard label="Total Booking" value={stats.totalBookings} />
        <StatCard label="Paid" value={stats.paid} variant="paid" />
        <StatCard label="Pending" value={stats.pending} variant="pending" />
        <StatCard label="Expired" value={stats.expired} variant="expired" />
        <StatCard
          label="Revenue Bulan Ini"
          value={fmtRupiah(stats.revenueMonth)}
          variant="revenue"
        />
        <StatCard
          label="Revenue Total"
          value={fmtRupiah(stats.revenueAllTime)}
          variant="revenue-total"
        />
      </div>

      {/* FILTER */}
      <div className="admin-bookings__filters">
        <select
          value={eventId}
          onChange={(e) => setEventId(e.target.value)}
          className="filter-select"
        >
          <option value="">Semua Event</option>
          {events.map((ev) => (
            <option key={ev._id} value={ev._id}>
              {ev.title}
            </option>
          ))}
        </select>

        <div className="search-box">
          <Search size={15} />
          <input
            placeholder="Cari nama / email / booking id / kode tiket"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <button
          className="sort-btn"
          onClick={() =>
            setSort((s) => (s === "newest" ? "oldest" : "newest"))
          }
          title={sort === "newest" ? "Terbaru" : "Terlama"}
        >
          <ArrowUpDown size={15} />
          <span>{sort === "newest" ? "Terbaru" : "Terlama"}</span>
        </button>
      </div>

      {/* 3 SECTIONS */}
      <div className="admin-bookings__sections">
        <BookingSection
          title="Menunggu Pembayaran"
          icon="⏳"
          status="PENDING"
          color="pending"
          search={search}
          eventId={eventId}
          sort={sort}
          refreshKey={refreshKey}
          onAction={() => setRefreshKey((k) => k + 1)}
        />
        <BookingSection
          title="Pembayaran Berhasil"
          icon="✅"
          status="PAID"
          color="paid"
          search={search}
          eventId={eventId}
          sort={sort}
          refreshKey={refreshKey}
          onAction={() => setRefreshKey((k) => k + 1)}
        />
        <BookingSection
          title="Expired"
          icon="🕐"
          status="EXPIRED"
          color="expired"
          search={search}
          eventId={eventId}
          sort={sort}
          defaultCollapsed
          refreshKey={refreshKey}
          onAction={() => setRefreshKey((k) => k + 1)}
        />
      </div>
    </div>
  );
}

/* ======================================================
   STAT CARD
====================================================== */
function StatCard({ label, value, variant }) {
  return (
    <div className={`stat-card ${variant || ""}`}>
      <span className="stat-card__label">{label}</span>
      <strong className="stat-card__value">{value}</strong>
    </div>
  );
}
